import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Smartphone, Target, TrendingUp, Users, Calendar, ExternalLink, Brain, Heart, BookOpen, RotateCcw, Trophy, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { applicationSchema, articleSchema, breadcrumbSchema } from '../utils/structuredData';

const DailyGlowPage = () => {
  const researchStats = [
    { multiplier: "3x", description: "More Likely to Succeed with Intention Setting", color: "text-ember-spark" },
    { multiplier: "70%", description: "Higher Success Rate with Values Alignment", color: "text-ember-fire" },
    { multiplier: "40%", description: "Increase in Emotional Resilience", color: "text-ember-heat" },
    { multiplier: "25%", description: "Higher Life Satisfaction", color: "text-ember-depth" }
  ];

  const intentionQuestions = [
    {
      number: "1",
      title: "Future Self Visioning",
      question: "Imagine it's one year from now and this daily reflection practice has genuinely transformed how you experience life. What's different about how you think, feel, or show up in your relationships and responsibilities?",
      science: "Activates your brain's \"prospective memory\" and triggers \"mental time travel\"—one of the most powerful motivators for behavior change. Your subconscious begins working toward your envisioned future automatically.",
      gradient: "from-ember-spark to-ember-glow"
    },
    {
      number: "2", 
      title: "Growth Edge Identification",
      question: "What's one recurring pattern in your thoughts, emotions, or reactions that you'd love to become more aware of—and perhaps gently shift over time?",
      science: "Activates your brain's \"attention bias\" through the \"frequency illusion.\" Once you identify a pattern, you'll suddenly notice it everywhere—the first step to lasting change.",
      gradient: "from-ember-fire to-ember-heat"
    },
    {
      number: "3",
      title: "Values Alignment", 
      question: "What matters most deeply to you in this season of life, and how do you hope becoming more reflective will help you live more aligned with those values?",
      science: "Values-based motivation is the most sustainable form of behavior change. When your practice connects to your deepest values, it transforms from \"should do\" to \"want to do.\"",
      gradient: "from-ember-depth to-ember-cool"
    }
  ];

  const glowQuestions = [
    {
      icon: Heart,
      emoji: "🙏",
      title: "GRATITUDE",
      question: "As you trace through your day, what moments, people, or experiences sparked gratitude? Share a few that stand out.",
      science: "Rewires neural pathways, strengthening your prefrontal cortex while calming your stress center. Results in 25% higher life satisfaction and better sleep quality.",
      why: "Trains your brain to notice positive moments as they happen, shifting your default mindset from scarcity to abundance.",
      gradient: "from-ember-spark to-ember-glow"
    },
    {
      icon: BookOpen,
      emoji: "📚", 
      title: "LEARNING",
      question: "What new insight did you gain today - about yourself, others, or the world around you?",
      science: "Triggers neuroplasticity and builds self-awareness. People who regularly reflect on learning are 23% more effective at problem-solving.",
      why: "Transforms ordinary experiences into growth opportunities, turning you into a learning machine that extracts wisdom from every day.",
      gradient: "from-ember-fire to-ember-heat"
    },
    {
      icon: RotateCcw,
      emoji: "🔄",
      title: "OPPORTUNITY", 
      question: "Reflect on a moment today that could have gone better. What wisdom will you carry forward to handle similar situations?",
      science: "Reframes \"failure\" as \"data,\" activating post-traumatic growth. Increases emotional resilience by 40% and reduces future anxiety.",
      why: "Builds emotional intelligence and creates a personal playbook for handling life's challenges with wisdom instead of reactivity.",
      gradient: "from-ember-heat to-ember-depth"
    },
    {
      icon: Trophy,
      emoji: "🏆",
      title: "WIN",
      question: "What did you accomplish today that deserves recognition? It could be a major milestone or a small step forward.",
      science: "Combats your brain's negativity bias. People who regularly acknowledge wins have 31% higher energy levels and achieve goals more consistently.",
      why: "Success builds on success. Recognizing daily progress builds unstoppable momentum and unshakeable confidence in your ability to grow.",
      gradient: "from-ember-depth to-ember-cool"
    }
  ];

  const neuralSteps = [
    { icon: Heart, title: "GRATITUDE", description: "Activates parasympathetic nervous system, shifting to rest-and-digest mode", color: "text-ember-spark" },
    { icon: BookOpen, title: "LEARNING", description: "Engages hippocampus and prefrontal cortex, consolidating experiences", color: "text-ember-fire" }, 
    { icon: RotateCcw, title: "OPPORTUNITY", description: "Activates anterior cingulate cortex for emotional regulation", color: "text-ember-heat" },
    { icon: Trophy, title: "WIN", description: "Stimulates reward system, releasing dopamine and positive associations", color: "text-ember-depth" }
  ];
  const features = [
    {
      icon: Target,
      title: "Daily Micro-Actions",
      description: "Small, consistent actions that compound into transformational growth over time."
    },
    {
      icon: TrendingUp,
      title: "Progress Tracking",
      description: "Visual insights into your leadership development journey with meaningful metrics."
    },
    {
      icon: Users,
      title: "Community Challenges",
      description: "Join fellow leaders in monthly challenges that push you beyond your comfort zone."
    },
    {
      icon: Calendar,
      title: "Habit Stacking",
      description: "Build leadership practices into your existing routines for sustainable change."
    }
  ];

  const testimonials = [
    {
      quote: "DailyGLOW has become my leadership compass. The daily prompts keep me centered on what matters most.",
      author: "Jennifer Walsh",
      role: "Director of Operations",
      company: "Tech Startup"
    },
    {
      quote: "I love how the app makes leadership development feel achievable. Just 5 minutes a day has transformed my mindset.",
      author: "Carlos Rodriguez", 
      role: "Team Lead",
      company: "Fortune 500 Company"
    },
    {
      quote: "The community aspect is incredible. Knowing other leaders are on the same journey keeps me motivated.",
      author: "Dr. Sarah Kim",
      role: "Department Head",
      company: "Research University"
    }
  ];

  const pageSchema = [
    applicationSchema,
    articleSchema({
      title: "DailyGLOW - Science-Backed Leadership Development App",
      description: "Discover the research-backed psychology that makes DailyGLOW effective at creating lasting leadership transformation through daily reflection practices.",
      url: "https://ember-leadership.com/dailyglow",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=2070&auto=format&fit=crop",
      datePublished: "2024-01-01T00:00:00Z",
      dateModified: new Date().toISOString()
    }),
    breadcrumbSchema([
      { name: "Home", url: "https://ember-leadership.com/" },
      { name: "DailyGLOW", url: "https://ember-leadership.com/dailyglow" }
    ])
  ];

  return (
    <>
      <SEOHead 
        title="DailyGLOW™ - Science-Backed Leadership Development App"
        description="Sustain your leadership growth through daily practice. A micro-learning app that helps you integrate transformational leadership principles into your everyday life with research-backed GLOW methodology."
        keywords="daily reflection app, leadership development app, GLOW methodology, gratitude practice, learning reflection, neural pathway training, leadership habits, micro-learning, personal growth app, behavioral psychology, leadership transformation"
        ogImage="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=2070&auto=format&fit=crop"
        canonicalUrl="/dailyglow"
        structuredData={pageSchema}
      />
      <div className="min-h-screen bg-neutral-warm">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-neutral-warm via-white to-neutral-warm relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-32 h-32 bg-ember-spark rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-ember-flow rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
        </div>

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          {/* Back Link */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-8"
          >
            <Link to="/" className="inline-flex items-center text-ember-fire hover:text-ember-heat transition-colors font-medium">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold text-neutral-charcoal mb-6 leading-tight">
                Daily<span className="bg-ember-gradient bg-clip-text text-transparent">GLOW</span>™
              </h1>
              <p className="text-xl text-neutral-medium mb-8 leading-relaxed">
                Sustain your leadership growth through daily practice. A micro-learning app that helps 
                you integrate transformational leadership principles into your everyday life.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <motion.a
                  href="https://glowforimpact.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-lg px-8 py-4 inline-flex items-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Visit DailyGLOW™ App
                  <ExternalLink className="w-4 h-4 ml-2" />
                </motion.a>
                <motion.button
                  className="btn-secondary text-lg px-8 py-4"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Learn More
                </motion.button>
              </div>

              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-ember-fire">5</div>
                  <div className="text-sm text-neutral-medium">Minutes/Day</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-ember-fire">Coming</div>
                  <div className="text-sm text-neutral-medium">Soon</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-ember-fire">Free</div>
                  <div className="text-sm text-neutral-medium">To Start</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative flex justify-center"
            >
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40"
                  alt="DailyGLOW App Interface"
                  className="rounded-2xl shadow-2xl w-80 h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-ember-spark/20 to-ember-flow/20 rounded-2xl"></div>
                
                {/* Phone Frame Overlay */}
                <div className="absolute -inset-4 border-8 border-neutral-charcoal rounded-3xl opacity-20"></div>
                <Smartphone className="absolute top-2 left-1/2 transform -translate-x-1/2 w-6 h-6 text-neutral-charcoal opacity-30" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>



      {/* The Science Behind GLOW */}
      <section className="py-20 bg-gradient-to-br from-neutral-charcoal to-ember-depth text-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              The Science Behind GLOW
            </h2>
            <p className="text-xl opacity-90 max-w-4xl mx-auto">
              Discover the research-backed psychology that makes DailyGLOW™ so effective at creating lasting transformation
            </p>
          </motion.div>

          {/* Research Stats */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h3 className="text-2xl font-bold mb-4">Research-Backed Results</h3>
              <p className="text-lg opacity-75">Every aspect of DailyGLOW™ is grounded in decades of behavioral psychology research</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {researchStats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
                >
                  <div className={`text-4xl font-bold mb-2 ${stat.color}`}>
                    {stat.multiplier}
                  </div>
                  <div className="text-sm opacity-75 leading-relaxed">
                    {stat.description}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intention Setting */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-neutral-charcoal mb-6">
              The Power of Intention Setting
            </h2>
            <p className="text-xl text-neutral-medium max-w-4xl mx-auto">
              Before users begin their DailyGLOW™ journey, we guide them through three strategic questions. 
              This isn't just nice-to-have—it's scientifically essential.
            </p>
          </motion.div>

          <div className="space-y-12">
            {intentionQuestions.map((question, index) => (
              <motion.div
                key={question.number}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="grid lg:grid-cols-2 gap-8 items-center"
              >
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="flex items-start mb-6">
                    <div className={`w-12 h-12 bg-gradient-to-r ${question.gradient} rounded-xl flex items-center justify-center text-white font-bold text-xl mr-4 flex-shrink-0`}>
                      {question.number}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-neutral-charcoal mb-2">{question.title}</h3>
                    </div>
                  </div>
                  <blockquote className="text-lg text-neutral-medium italic mb-6 leading-relaxed pl-4 border-l-4 border-ember-spark">
                    "{question.question}"
                  </blockquote>
                  <div className="bg-gradient-to-br from-neutral-warm to-white p-6 rounded-xl border border-neutral-light">
                    <h4 className="font-bold text-ember-fire mb-2 flex items-center">
                      <Brain className="w-5 h-5 mr-2" />
                      The Science
                    </h4>
                    <p className="text-neutral-dark leading-relaxed">{question.science}</p>
                  </div>
                </div>
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''} flex justify-center`}>
                  <div className={`w-80 h-80 bg-gradient-to-br ${question.gradient} rounded-3xl shadow-2xl flex items-center justify-center`}>
                    <Brain className="w-24 h-24 text-white opacity-80" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GLOW Framework */}
      <section className="py-20 bg-gradient-to-br from-neutral-warm to-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-neutral-charcoal mb-6">
              The Four DailyGLOW™ Questions
            </h2>
            <p className="text-xl text-neutral-medium max-w-4xl mx-auto">
              Each question targets specific neural pathways that, when strengthened daily, 
              create measurable shifts in cognitive and emotional functioning.
            </p>
          </motion.div>

          <div className="space-y-16">
            {glowQuestions.map((question, index) => {
              const IconComponent = question.icon;
              return (
                <motion.div
                  key={question.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-3xl p-8 shadow-xl border border-neutral-light"
                >
                  <div className="grid lg:grid-cols-3 gap-8 items-start">
                    <div className="lg:col-span-1">
                      <div className="flex items-center mb-6">
                        <div className={`w-16 h-16 bg-gradient-to-br ${question.gradient} rounded-2xl flex items-center justify-center mr-4`}>
                          <span className="text-2xl">{question.emoji}</span>
                        </div>
                        <h3 className="text-2xl font-bold text-neutral-charcoal">{question.title}</h3>
                      </div>
                      <blockquote className="text-lg text-neutral-medium italic leading-relaxed">
                        "{question.question}"
                      </blockquote>
                    </div>
                    
                    <div className="lg:col-span-2 space-y-6">
                      <div className="bg-gradient-to-br from-ember-spark/10 to-ember-flow/10 p-6 rounded-xl">
                        <h4 className="font-bold text-ember-fire mb-3 flex items-center">
                          <Brain className="w-5 h-5 mr-2" />
                          The Science
                        </h4>
                        <p className="text-neutral-dark leading-relaxed mb-4">{question.science}</p>
                        <h4 className="font-bold text-ember-depth mb-2 flex items-center">
                          <Zap className="w-5 h-5 mr-2" />
                          Why It Works
                        </h4>
                        <p className="text-neutral-dark leading-relaxed">{question.why}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Neural Sequence */}
      <section className="py-20 bg-gradient-to-br from-ember-depth to-ember-cool text-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">Why the Sequence Matters</h2>
            <p className="text-xl opacity-90 max-w-4xl mx-auto">
              The order isn't random. Each question primes your brain for the next, creating a complete mental reset.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <h3 className="text-2xl font-bold mb-4 flex items-center">
                <Brain className="w-8 h-8 mr-3 text-ember-spark" />
                Psychological Priming
              </h3>
              <h4 className="text-lg font-semibold mb-3 text-ember-glow">The Flow</h4>
              <p className="opacity-90 leading-relaxed">
                Gratitude opens your heart → Learning engages your mind → Opportunity builds resilience → Win creates momentum. 
                This sequence optimizes your emotional and cognitive state for maximum growth.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
            >
              <h3 className="text-2xl font-bold mb-4 flex items-center">
                <Zap className="w-8 h-8 mr-3 text-ember-spark" />
                Amplified Impact
              </h3>
              <h4 className="text-lg font-semibold mb-3 text-ember-glow">Synergistic Effect</h4>
              <p className="opacity-90 leading-relaxed">
                Each question amplifies the others. Starting with gratitude makes you more receptive to learning. 
                Ending with wins creates positive momentum that carries into tomorrow. The whole becomes greater than the sum of its parts.
              </p>
            </motion.div>
          </div>

          {/* Neural Reset Steps */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20"
          >
            <h3 className="text-3xl font-bold text-center mb-8">The Complete Neural Reset</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {neuralSteps.map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center"
                  >
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-8 h-8 text-ember-spark" />
                    </div>
                    <h4 className="font-bold mb-2 text-ember-glow">{step.title}</h4>
                    <p className="text-sm opacity-80 leading-relaxed">{step.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final Science Summary */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl font-bold text-neutral-charcoal mb-8">
              This Isn't Just Positive Thinking—It's Positive Re-wiring
            </h2>
            <p className="text-xl text-neutral-medium max-w-4xl mx-auto mb-12 leading-relaxed">
              Every aspect of DailyGLOW™ works with your brain's natural tendencies. We're systematically strengthening 
              the neural pathways that research shows lead to greater resilience, wisdom, gratitude, and growth.
            </p>
            <p className="text-lg text-neutral-dark max-w-4xl mx-auto mb-12 leading-relaxed">
              The result is a daily practice that creates measurable, lasting changes in how you experience and navigate life—not 
              just temporary mood improvements, but fundamental shifts in psychological functioning.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-gradient-to-br from-ember-spark to-ember-glow p-6 rounded-2xl text-white text-center">
                <h3 className="font-bold text-lg mb-2">Science-Backed</h3>
                <p className="text-sm opacity-90">Based on decades of behavioral psychology research</p>
              </div>
              <div className="bg-gradient-to-br from-ember-fire to-ember-heat p-6 rounded-2xl text-white text-center">
                <h3 className="font-bold text-lg mb-2">Research-Proven</h3>
                <p className="text-sm opacity-90">Measurable improvements in 2-4 weeks</p>
              </div>
              <div className="bg-gradient-to-br from-ember-depth to-ember-cool p-6 rounded-2xl text-white text-center">
                <h3 className="font-bold text-lg mb-2">Results-Driven</h3>
                <p className="text-sm opacity-90">Systematic neural pathway strengthening</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-neutral-charcoal to-ember-depth text-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">Coming Soon</h2>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              DailyGLOW™ is currently in development. Be the first to experience the power of daily leadership reflection 
              when we launch. Join our waitlist to get early access and exclusive launch benefits.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold mb-4">Join the Waitlist</h3>
              <p className="opacity-90 mb-6">Get notified when DailyGLOW™ launches and receive exclusive early access.</p>
              <Link to="/waitlist" className="inline-block">
                <motion.button
                  className="bg-ember-spark text-neutral-charcoal px-8 py-4 rounded-xl font-bold text-lg hover:bg-ember-glow transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Join DailyGLOW™ Waitlist
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-charcoal mb-8">
              Ready to Transform Your Daily Practice?
            </h2>
            <p className="text-xl text-neutral-medium mb-12 leading-relaxed">
              Be the first to experience DailyGLOW™ when we launch. Join the waitlist for early access 
              and exclusive benefits as we finalize this transformational daily practice app.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <motion.a
                href="https://glowforimpact.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-lg px-12 py-4 ember-glow inline-flex items-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Start Your Daily Practice
                <ExternalLink className="w-5 h-5 ml-2" />
              </motion.a>
              <motion.button
                className="btn-secondary text-lg px-12 py-4"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Learn More About EMBER
              </motion.button>
            </div>

            <div className="mt-12 pt-8 border-t border-neutral-light">
              <p className="text-sm text-neutral-medium">
                Free to download • Premium features available • iOS & Android
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
    </>
  );
};

export default DailyGlowPage;